import type { VideoPlayerConfig } from "@/generated/sb";

// Scheme is optional: editors sometimes paste `fast.wistia.net/...` as is.
const WISTIA_HOST =
  /^(?:https?:)?(?:\/\/)?(?:[a-z0-9-]+\.)*wistia\.(?:com|net)(?:[/?#:]|$)/i;

export function extractWistiaId(url: string | undefined): string | undefined {
  if (!url || !WISTIA_HOST.test(url)) return undefined;

  let match = url.match(/\/iframe\/([a-zA-Z0-9]+)/);
  if (match) return match[1];

  match = url.match(/medias?\/([a-zA-Z0-9]+)/);
  return match ? match[1] : undefined;
}

export function extractVideoConfig(
  configuration: any,
): VideoPlayerConfig | undefined {
  if (!configuration) return undefined;

  if (typeof configuration === "object" && "content" in configuration) {
    return configuration.content;
  }

  return undefined;
}

const WISTIA_SHARE_LINK =
  /^https?:\/\/[a-z0-9-]+\.wistia\.(?:com|net)\/s\/[a-zA-Z0-9]+/i;

export function isWistiaShareLink(url: string | undefined): url is string {
  return !!url && WISTIA_SHARE_LINK.test(url);
}

// Share links (`/s/<id>`) carry an ID that is not the media ID, so the only
// way to get a playable ID is Wistia's oEmbed endpoint. It sends
// `Access-Control-Allow-Origin: *`, so it can be called from the browser.
export async function fetchWistiaShareLinkId(
  url: string,
): Promise<string | undefined> {
  try {
    const res = await fetch(
      `https://fast.wistia.com/oembed?url=${encodeURIComponent(url)}`,
    );
    if (!res.ok) throw new Error(`oEmbed responded ${res.status}`);
    const { html } = (await res.json()) as { html?: string };
    return html?.match(/\/iframe\/([a-zA-Z0-9]+)/)?.[1];
  } catch (error) {
    console.warn("Failed to resolve Wistia share link", url, error);
    return undefined;
  }
}
